import React, { useState, useEffect, useRef } from 'react';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, CheckCircle, Crown, GripVertical, Pencil, Check, X, FolderPlus, CalendarDays, Clock, Repeat, Flame, Snowflake, ArrowUpDown, ShoppingCart, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '@/hooks/useFetch';
import { GroceryList } from '@/components/GroceryList';

const CHECKLIST_FREE_LIMIT = 5;
const STORAGE_KEY = 'clearcomm-todos';
const CATEGORIES_KEY = 'clearcomm-todo-categories';

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'No repeat', labelEs: 'Sin repetir' },
  { value: 'daily', label: 'Daily', labelEs: 'Diario' },
  { value: 'weekly', label: 'Weekly', labelEs: 'Semanal' },
  { value: 'monthly', label: 'Monthly', labelEs: 'Mensual' },
];

const getNextDueDate = (currentDue, recurrence) => {
  const base = currentDue ? new Date(currentDue + 'T00:00:00') : new Date();
  switch (recurrence) {
    case 'daily': base.setDate(base.getDate() + 1); break;
    case 'weekly': base.setDate(base.getDate() + 7); break;
    case 'monthly': base.setMonth(base.getMonth() + 1); break;
    default: return null;
  }
  return base.toISOString().split('T')[0];
};

const DEFAULT_CATEGORIES = [
  { id: 'personal', name: 'Personal', color: '#3B82F6' },
  { id: 'work', name: 'Work', color: '#F59E0B' },
  { id: 'health', name: 'Health', color: '#10B981' },
  { id: 'learning', name: 'Learning', color: '#8B5CF6' },
];

const DEFAULT_CATEGORIES_ES = [
  { id: 'personal', name: 'Personal', color: '#3B82F6' },
  { id: 'work', name: 'Trabajo', color: '#F59E0B' },
  { id: 'health', name: 'Salud', color: '#10B981' },
  { id: 'learning', name: 'Aprendizaje', color: '#8B5CF6' },
];

const TodoPage = () => {
  const { t, isSpanish } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [todos, setTodos] = useState([]);
  const [newTodo, setNewTodo] = useState('');
  const [newCategory, setNewCategory] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryColor, setNewCategoryColor] = useState('#6366F1');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editCategory, setEditCategory] = useState(null);
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [newDueDate, setNewDueDate] = useState(null);
  const [newRecurrence, setNewRecurrence] = useState('none');
  const [filterDue, setFilterDue] = useState('all');
  const [sortBy, setSortBy] = useState('custom'); // custom, alpha-asc, alpha-desc, due-asc, due-desc
  const [taskStreak, setTaskStreak] = useState({ current: 0, longest: 0, tasks_today: 0, freeze_active: false });
  const [showAddDialog, setShowAddDialog] = useState(false);
  // Tasks vs Grocery List tab — deep-linkable via /todo?tab=grocery
  const [pageTab, setPageTab] = useState(() =>
    new URLSearchParams(window.location.search).get('tab') === 'grocery' ? 'grocery' : 'tasks'
  );
  const editInputRef = useRef(null);
  const dataLoaded = useRef(false);
  const hasSynced = useRef(false);

  const isLoggedIn = !!user;
  const isVip = user?.subscription_tier && user.subscription_tier !== 'free';
  const activeTodos = todos.filter(t => !t.completed);
  const canAddMore = true; // No task limits — VIP is ad-free only

  // STEP 1: Always load from localStorage first (immediate, synchronous)
  useEffect(() => {
    const savedTodos = localStorage.getItem(STORAGE_KEY);
    if (savedTodos) {
      try { setTodos(JSON.parse(savedTodos)); } catch (e) { console.warn('Failed to parse saved todos', e); }
    }
    loadLocalCategories();
    dataLoaded.current = true;
  }, []);

  // STEP 2: If logged in, overlay with server data (or migrate localStorage → server)
  useEffect(() => {
    if (isLoggedIn && !hasSynced.current) {
      hasSynced.current = true;
      syncWithServer();
    }
  }, [isLoggedIn]);

  const loadLocalCategories = () => {
    const savedCats = localStorage.getItem(CATEGORIES_KEY);
    const parsedCats = savedCats ? JSON.parse(savedCats) : null;
    // Filter out default categories that have no todos using them
    const defaultIds = new Set(DEFAULT_CATEGORIES.map(d => d.id));
    if (parsedCats && parsedCats.length > 0) {
      const savedTodos = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const usedCatIds = new Set(savedTodos.map(t => t.category).filter(Boolean));
      const filtered = parsedCats.filter(c => !defaultIds.has(c.id) || usedCatIds.has(c.id));
      setCategories(filtered);
      localStorage.setItem(CATEGORIES_KEY, JSON.stringify(filtered));
    }
    // No auto-creation — defaults are shown as suggestions in the dropdown
  };

  const syncWithServer = async () => {
    const [todosRes, catsRes, streakRes] = await Promise.all([
      apiFetch('/api/todos'),
      apiFetch('/api/todos/categories'),
      apiFetch('/api/todos/streak'),
    ]);

    if (todosRes.data?.todos?.length > 0) {
      // Server has data — use it as source of truth
      setTodos(todosRes.data.todos);
      // Also keep localStorage in sync as backup
      localStorage.setItem(STORAGE_KEY, JSON.stringify(todosRes.data.todos));
    } else {
      // Server empty — migrate current state (from localStorage) to server.
      // Only sync if we actually have local data to send; never POST an
      // empty payload that could wipe a partial server backup.
      const currentTodos = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      const currentCats = JSON.parse(localStorage.getItem(CATEGORIES_KEY) || '[]');
      if (currentTodos.length > 0) {
        await apiFetch('/api/todos/sync', {
          method: 'POST',
          body: { todos: currentTodos, categories: currentCats },
        });
        // Re-fetch so we display server-side ids/order.
        const r2 = await apiFetch('/api/todos');
        if (r2.data?.todos) setTodos(r2.data.todos);
      }
    }

    if (catsRes.data?.categories?.length > 0) {
      // Filter out default categories that have no todos using them
      const defaultIds = new Set(DEFAULT_CATEGORIES.map(d => d.id));
      const serverTodos = todosRes.data?.todos || [];
      const usedCatIds = new Set(serverTodos.map(t => t.category).filter(Boolean));
      const filtered = catsRes.data.categories.filter(c => !defaultIds.has(c.id) || usedCatIds.has(c.id));
      setCategories(filtered);
      localStorage.setItem(CATEGORIES_KEY, JSON.stringify(filtered));
      
      // Also clean up server-side if we removed unused defaults
      if (filtered.length < catsRes.data.categories.length) {
        apiFetch('/api/todos/categories/sync', {
          method: 'POST',
          body: { categories: filtered }
        }).catch(() => {});
      }
    } else {
      // Server has no categories yet. If the user had custom categories saved
      // locally (e.g. they created some before signing in), migrate them up to
      // the server instead of wiping them. This prevents the "my custom
      // categories disappeared after I logged in" bug.
      const localCats = JSON.parse(localStorage.getItem(CATEGORIES_KEY) || '[]');
      if (localCats.length > 0) {
        apiFetch('/api/todos/categories/sync', {
          method: 'POST',
          body: { categories: localCats }
        }).catch(() => {});
        setCategories(localCats);
      }
    }

    if (streakRes.data) {
      setTaskStreak(streakRes.data);
    }
  };

  // Save to localStorage as persistent backup (only after initial load)
  useEffect(() => {
    if (dataLoaded.current && todos.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(todos));
    }
  }, [todos]);
  useEffect(() => {
    if (dataLoaded.current && categories.length > 0) {
      localStorage.setItem(CATEGORIES_KEY, JSON.stringify(categories));
    }
  }, [categories]);

  // Focus edit input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) editInputRef.current.focus();
  }, [editingId]);

  const addTodo = async (e) => {
    e.preventDefault();
    if (!newTodo.trim()) return;
    
    // Resolve category - handle custom typed categories
    let resolvedCategory = null;
    if (newCategory && newCategory !== 'all') {
      const existingCat = categories.find(c => c.id === newCategory || c.name.toLowerCase() === newCategory.toLowerCase());
      if (existingCat) {
        resolvedCategory = existingCat.id;
      } else {
        // Check defaults
        const defaults = isSpanish ? DEFAULT_CATEGORIES_ES : DEFAULT_CATEGORIES;
        const defaultCat = defaults.find(d => d.name.toLowerCase() === newCategory.toLowerCase() || d.id === newCategory);
        if (defaultCat) {
          // Add suggested category
          const updated = [...categories, defaultCat];
          setCategories(updated);
          if (isLoggedIn) {
            apiFetch('/api/todos/categories', { method: 'POST', body: { id: defaultCat.id, name: defaultCat.name, color: defaultCat.color } });
          }
          resolvedCategory = defaultCat.id;
        } else {
          // Create brand new custom category
          const customId = newCategory.trim().toLowerCase().replace(/\s+/g, '-');
          const customCat = { id: customId, name: newCategory.trim(), color: PRESET_COLORS[categories.length % PRESET_COLORS.length] || '#6366F1' };
          setCategories(prev => [...prev, customCat]);
          if (isLoggedIn) {
            apiFetch('/api/todos/categories', { method: 'POST', body: customCat });
          }
          resolvedCategory = customId;
        }
      }
    }
    
    const todo = {
      id: crypto.randomUUID(),
      text: newTodo.trim(),
      completed: false,
      category: resolvedCategory,
      dueDate: newDueDate ? newDueDate.toISOString().split('T')[0] : null,
      recurrence: newRecurrence !== 'none' ? newRecurrence : null,
      createdAt: new Date().toISOString(),
      order: todos.length
    };

    if (isLoggedIn) {
      await apiFetch('/api/todos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(todo),
      });
    }

    setTodos(prev => [todo, ...prev]);
    setNewTodo('');
    setNewDueDate(null);
    setNewRecurrence('none');
    toast.success(isSpanish ? 'Tarea agregada!' : 'Task added!');
  };

  const toggleTodo = (id) => {
    setTodos(prev => {
      const todo = prev.find(t => t.id === id);
      if (!todo) return prev;

      if (!todo.completed) {
        if (isLoggedIn) {
          // Call backend complete endpoint (handles recurring + streak)
          apiFetch(`/api/todos/${id}/complete`, { method: 'POST' }).then(res => {
            if (res.data) {
              const { next_todo, streak, xp } = res.data;
              if (streak) setTaskStreak(s => ({ ...s, ...streak }));
              if (xp?.earned) {
                toast.success(
                  isSpanish
                    ? `+${xp.earned} XP! Racha de tareas: ${streak?.current || 0}`
                    : `+${xp.earned} XP! Task streak: ${streak?.current || 0}`
                );
              }
              setTodos(current => {
                const without = current.filter(t => t.id !== id);
                return next_todo ? [next_todo, ...without] : without;
              });
            }
          });
          return prev.map(t => t.id === id ? { ...t, completed: true } : t);
        }

        // Guest: local-only logic
        if (todo.recurrence) {
          const nextDue = getNextDueDate(todo.dueDate, todo.recurrence);
          const nextTodo = {
            id: crypto.randomUUID(),
            text: todo.text,
            completed: false,
            category: todo.category,
            dueDate: nextDue,
            recurrence: todo.recurrence,
            createdAt: new Date().toISOString(),
            order: 0
          };
          setTimeout(() => {
            setTodos(current => [nextTodo, ...current.filter(t => t.id !== id)]);
            toast.success(isSpanish ? 'Tarea completada! Siguiente creada.' : 'Task done! Next one created.');
          }, 500);
          return prev.map(t => t.id === id ? { ...t, completed: true } : t);
        }

        setTimeout(() => {
          setTodos(current => current.filter(t => t.id !== id));
          toast.success(isSpanish ? 'Tarea completada!' : 'Task completed!');
        }, 500);
      }
      return prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t);
    });
  };

  const deleteTodo = async (id) => {
    if (isLoggedIn) {
      await apiFetch(`/api/todos/${id}`, { method: 'DELETE' });
    }
    setTodos(prev => prev.filter(todo => todo.id !== id));
    toast.info(isSpanish ? 'Tarea eliminada' : 'Task removed');
  };

  const startEdit = (todo) => {
    setEditingId(todo.id);
    setEditText(todo.text);
    setEditCategory(todo.category || 'all');
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    const catValue = editCategory === 'all' ? null : editCategory;
    if (isLoggedIn) {
      await apiFetch(`/api/todos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: editText.trim(), category: catValue }),
      });
    }
    setTodos(prev => prev.map(t => t.id === id ? { ...t, text: editText.trim(), category: catValue } : t));
    setEditingId(null);
    setEditText('');
    setEditCategory(null);
    // Auto-add suggested category if it was selected
    if (catValue) {
      const defaults = isSpanish ? DEFAULT_CATEGORIES_ES : DEFAULT_CATEGORIES;
      const suggested = defaults.find(d => d.id === catValue);
      if (suggested && !categories.find(c => c.id === catValue)) {
        const updated = [...categories, suggested];
        setCategories(updated);
        if (isLoggedIn) {
          apiFetch('/api/todos/categories', { method: 'POST', body: { id: suggested.id, name: suggested.name, color: suggested.color } });
        }
      }
    }
  };

  const cancelEdit = () => { setEditingId(null); setEditText(''); setEditCategory(null); };

  const addCategory = (e) => {
    e.preventDefault();
    if (!newCategoryName.trim()) return;
    const id = newCategoryName.trim().toLowerCase().replace(/\s+/g, '-');
    if (categories.find(c => c.id === id)) {
      toast.error(isSpanish ? 'Categoria ya existe' : 'Category already exists');
      return;
    }
    setCategories(prev => [...prev, { id, name: newCategoryName.trim(), color: newCategoryColor }]);
    setNewCategoryName('');
    setShowNewCategory(false);
    toast.success(isSpanish ? 'Categoria creada!' : 'Category created!');
  };

  // Drag and drop handlers
  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (id !== draggedId) setDragOverId(id);
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetId) return;
    setTodos(prev => {
      const items = [...prev];
      const dragIdx = items.findIndex(t => t.id === draggedId);
      const dropIdx = items.findIndex(t => t.id === targetId);
      const [dragged] = items.splice(dragIdx, 1);
      items.splice(dropIdx, 0, dragged);
      if (isLoggedIn) {
        apiFetch('/api/todos/reorder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ordered_ids: items.map(t => t.id) }),
        });
      }
      return items;
    });
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => { setDraggedId(null); setDragOverId(null); };

  // Filter todos
  const today = new Date().toISOString().split('T')[0];
  const weekFromNow = new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
  const twoWeeksFromNow = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0];

  let filteredTodos = filterCategory === 'all'
    ? todos
    : todos.filter(t => t.category === filterCategory);

  if (filterDue === 'today') {
    filteredTodos = filteredTodos.filter(t => t.dueDate === today);
  } else if (filterDue === 'week') {
    filteredTodos = filteredTodos.filter(t => t.dueDate && t.dueDate >= today && t.dueDate <= weekFromNow);
  } else if (filterDue === 'next-week') {
    // Strictly the 7 days AFTER "this week" (days +7 through +14)
    filteredTodos = filteredTodos.filter(t => t.dueDate && t.dueDate > weekFromNow && t.dueDate <= twoWeeksFromNow);
  } else if (filterDue === 'overdue') {
    filteredTodos = filteredTodos.filter(t => t.dueDate && t.dueDate < today && !t.completed);
  }

  // Sort todos
  if (sortBy !== 'custom') {
    filteredTodos = [...filteredTodos].sort((a, b) => {
      if (sortBy === 'alpha-asc') return a.text.localeCompare(b.text);
      if (sortBy === 'alpha-desc') return b.text.localeCompare(a.text);
      if (sortBy === 'category') {
        // Group by category name; "no category" items sink to the bottom.
        const an = a.category ? (categories.find(c => c.id === a.category)?.name || '~') : '~~';
        const bn = b.category ? (categories.find(c => c.id === b.category)?.name || '~') : '~~';
        return an.localeCompare(bn);
      }
      if (sortBy === 'due-asc') {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return a.dueDate.localeCompare(b.dueDate);
      }
      if (sortBy === 'due-desc') {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return b.dueDate.localeCompare(a.dueDate);
      }
      return 0;
    });
  }

  const getDueDateLabel = (dueDate) => {
    if (!dueDate) return null;
    // Happy vibrant palette — no red/yellow/orange anywhere on this page
    if (dueDate === today) return { text: isSpanish ? 'Hoy' : 'Today', color: 'text-[#00A896] dark:text-[#00E5CC] font-semibold' };
    if (dueDate < today) return { text: isSpanish ? '¡Aún pendiente!' : 'Still pending (Whoops!)', color: 'text-[#7C3AED] dark:text-[#A78BFA] font-semibold' };
    const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
    if (dueDate === tomorrow) return { text: isSpanish ? 'Mañana' : 'Tomorrow', color: 'text-[#3B82F6] dark:text-[#60A5FA]' };
    return { text: new Date(dueDate + 'T00:00:00').toLocaleDateString(isSpanish ? 'es' : 'en', { month: 'short', day: 'numeric' }), color: 'text-muted-foreground' };
  };

  const getCategoryById = (id) => categories.find(c => c.id === id);

  const PRESET_COLORS = ['#3B82F6', '#F59E0B', '#10B981', '#8B5CF6', '#EF4444', '#EC4899', '#06B6D4', '#F97316'];

  return (
    <div className="min-h-screen bg-background pb-20">
      <Header />

      <section className="py-6 sm:py-8">
        <div className="container max-w-2xl">
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              <span className="bg-gradient-to-r from-[#00E5CC] to-[#7C3AED] bg-clip-text text-transparent">
                {t('todoTitle')}
              </span>
            </h1>
            <p className="text-muted-foreground text-sm">{t('todoDescription')}</p>
          </div>

          {/* Tasks / Grocery tab switch */}
          <div className="flex justify-center gap-2 mb-6" data-testid="todo-page-tabs">
            <button
              onClick={() => setPageTab('tasks')}
              data-testid="tasks-tab-btn"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                pageTab === 'tasks' ? 'bg-[#00E5CC] text-[#0d1f2d]' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <ListTodo className="h-4 w-4" />
              {isSpanish ? 'Tareas' : 'Tasks'}
            </button>
            <button
              onClick={() => setPageTab('grocery')}
              data-testid="grocery-tab-btn"
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all flex items-center gap-1.5 ${
                pageTab === 'grocery' ? 'bg-[#00E5CC] text-[#0d1f2d]' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <ShoppingCart className="h-4 w-4" />
              {isSpanish ? 'Lista de Compras' : 'Grocery List'}
            </button>
          </div>

          {pageTab === 'grocery' && <GroceryList isSpanish={isSpanish} isLoggedIn={isLoggedIn} userId={user?.user_id} />}

          {pageTab === 'tasks' && (<>
          {/* Task Streak Card */}
          {isLoggedIn && (
            <div className="flex items-center justify-center gap-4 mb-5 px-4 py-3 rounded-xl bg-gradient-to-r from-[#00E5CC]/10 to-[#7C3AED]/10 border border-[#00E5CC]/20" data-testid="task-streak-card">
              <div className="flex items-center gap-1.5">
                <Flame className="h-5 w-5 text-orange-500" />
                <div>
                  <p className="text-lg font-bold leading-none">{taskStreak.current}</p>
                  <p className="text-[10px] text-muted-foreground">{isSpanish ? 'Racha' : 'Streak'}</p>
                </div>
              </div>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-1.5">
                <span className="text-sm">🏆</span>
                <div>
                  <p className="text-lg font-bold leading-none">{taskStreak.longest}</p>
                  <p className="text-[10px] text-muted-foreground">{isSpanish ? 'Mejor' : 'Best'}</p>
                </div>
              </div>
              <div className="w-px h-8 bg-border" />
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-[#00E5CC]" />
                <div>
                  <p className="text-lg font-bold leading-none">{taskStreak.tasks_today}</p>
                  <p className="text-[10px] text-muted-foreground">{isSpanish ? 'Hoy' : 'Today'}</p>
                </div>
              </div>
              {taskStreak.freeze_active && (
                <>
                  <div className="w-px h-8 bg-border" />
                  <div className="flex items-center gap-1">
                    <Snowflake className="h-4 w-4 text-blue-400" />
                    <span className="text-[10px] text-blue-500 font-medium">{isSpanish ? 'Congelada' : 'Frozen'}</span>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Floating Add button — REMOVED May 2026 (was overlapping
              the DetectiveDinoBot help-bot at bottom-right). Replaced
              by the inline "Add task" button to the LEFT of the sort
              selector above. */}

          {/* Add Todo Dialog */}
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{isSpanish ? 'Nueva Tarea' : 'New Task'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={(e) => { addTodo(e); setShowAddDialog(false); }} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">{isSpanish ? 'Tarea' : 'Task'}</Label>
                  <Input
                    value={newTodo}
                    onChange={(e) => setNewTodo(e.target.value)}
                    placeholder={isSpanish ? 'Describe tu tarea...' : 'Describe your task...'}
                    autoFocus
                    className="mt-1"
                    data-testid="todo-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium">{isSpanish ? 'Categoría (opcional)' : 'Category (optional)'}</Label>
                  <div className="relative mt-1">
                    <Input
                      value={newCategory === 'all' ? '' : (categories.find(c => c.id === newCategory)?.name || newCategory)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (!val) { setNewCategory('all'); return; }
                        // Check if matches an existing category
                        const match = categories.find(c => c.name.toLowerCase() === val.toLowerCase());
                        if (match) { setNewCategory(match.id); }
                        else { setNewCategory(val); }
                      }}
                      placeholder={isSpanish ? 'Escribe o elige categoría...' : 'Type or pick a category...'}
                      className="text-sm"
                      data-testid="todo-category-input"
                      list="todo-category-suggestions"
                    />
                    <datalist id="todo-category-suggestions">
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.name} />
                      ))}
                      {(() => {
                        const defaults = isSpanish ? DEFAULT_CATEGORIES_ES : DEFAULT_CATEGORIES;
                        return defaults
                          .filter(d => !categories.find(c => c.id === d.id))
                          .map(cat => (
                            <option key={cat.id} value={cat.name} />
                          ));
                      })()}
                    </datalist>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">{isSpanish ? 'Fecha límite' : 'Due Date'}</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full mt-1 justify-start text-left font-normal text-sm">
                          <CalendarDays className="h-4 w-4 mr-2 shrink-0" />
                          {newDueDate ? new Date(newDueDate).toLocaleDateString() : (isSpanish ? 'Sin fecha' : 'No date')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar mode="single" selected={newDueDate ? new Date(newDueDate) : undefined} onSelect={(d) => setNewDueDate(d || null)} />
                        {newDueDate && (
                          <div className="px-3 pb-2">
                            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setNewDueDate(null)}>{isSpanish ? 'Sin fecha' : 'Clear date'}</Button>
                          </div>
                        )}
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">{isSpanish ? 'Repetir' : 'Repeat'}</Label>
                    <Select value={newRecurrence} onValueChange={setNewRecurrence}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {RECURRENCE_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{isSpanish ? opt.labelEs : opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* New category inline */}
                {!showNewCategory ? (
                  <Button type="button" variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={() => setShowNewCategory(true)}>
                    <FolderPlus className="h-3 w-3 mr-1" />
                    {isSpanish ? 'Nueva categoría' : 'New category'}
                  </Button>
                ) : (
                  <div className="flex gap-2 items-center border-t pt-3">
                    <Input value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} placeholder={isSpanish ? 'Nombre...' : 'Name...'} className="flex-1 h-8 text-sm" data-testid="new-category-input" />
                    <div className="flex gap-1">
                      {PRESET_COLORS.map(c => (
                        <button key={c} type="button" className={`w-5 h-5 rounded-full border-2 transition-all ${newCategoryColor === c ? 'border-foreground scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} onClick={() => setNewCategoryColor(c)} />
                      ))}
                    </div>
                    <Button type="button" size="sm" className="h-8 bg-[#00E5CC] hover:bg-[#00C9B2]" onClick={(e) => { addCategory(e); }} data-testid="save-category-btn">
                      <Check className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                <Button type="submit" className="w-full bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] font-medium" disabled={!newTodo.trim()}>
                  <Plus className="h-4 w-4 mr-2" />
                  {isSpanish ? 'Agregar Tarea' : 'Add Task'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Category filter tabs */}
          {categories.length > 0 && (
            <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 scrollbar-hide" data-testid="category-filters">
              <button
                onClick={() => setFilterCategory('all')}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  filterCategory === 'all'
                    ? 'bg-[#00E5CC] text-[#0d1f2d] font-semibold'
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {isSpanish ? 'Todas' : 'All'} ({todos.length})
              </button>
              {categories.map(cat => {
                const count = todos.filter(t => t.category === cat.id).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setFilterCategory(cat.id)}
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      filterCategory === cat.id
                        ? 'text-white'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                    style={filterCategory === cat.id ? { backgroundColor: cat.color } : {}}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: filterCategory === cat.id ? '#fff' : cat.color }} />
                    {cat.name} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {/* Due date filter */}
          <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1 scrollbar-hide" data-testid="due-date-filters">
            <Clock className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            {[
              { id: 'all',       label: isSpanish ? 'Todas' : 'All',                                  active: 'bg-[#00A896] text-white' },
              { id: 'today',     label: isSpanish ? 'Hoy' : 'Today',                                  active: 'bg-[#00E5CC] text-[#0d1f2d]' },
              { id: 'week',      label: isSpanish ? 'Esta semana' : 'This week',                      active: 'bg-[#3B82F6] text-white' },
              { id: 'next-week', label: isSpanish ? 'Próxima semana' : 'Next week',                   active: 'bg-[#8B5CF6] text-white' },
              { id: 'overdue',   label: isSpanish ? '¡Aún pendiente!' : 'Still pending (Whoops!)',    active: 'bg-gradient-to-r from-[#00E5CC] to-[#7C3AED] text-white' },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilterDue(f.id)}
                data-testid={`due-filter-${f.id}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  filterDue === f.id
                    ? f.active
                    : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Add task button (LEFT) + Sort selector (RIGHT)
              The FAB at bottom-right was removed because it overlapped
              the DetectiveDinoBot help-bot. Now `Add task` is inline at
              the left of this row and the sort dropdown floats to the
              right via `justify-between`. */}
          <div className="flex items-center justify-between gap-2 mb-4" data-testid="sort-selector">
            <Button
              onClick={() => { setShowAddDialog(true); setNewTodo(''); setNewCategory('all'); setNewDueDate(null); setNewRecurrence('none'); }}
              className="h-8 px-3 bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d] dark:text-[#0d1f2d] gap-1.5 text-sm shadow-sm font-semibold"
              disabled={!canAddMore}
              data-testid="add-todo-btn"
            >
              <Plus className="h-4 w-4" />
              <span>{isSpanish ? 'Añadir tarea' : 'Add task'}</span>
            </Button>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground shrink-0" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-7 text-xs w-auto min-w-[140px]" data-testid="sort-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">{isSpanish ? 'Orden manual' : 'Manual order'}</SelectItem>
                  <SelectItem value="category">{isSpanish ? 'Por categoría' : 'By category'}</SelectItem>
                  <SelectItem value="alpha-asc">{isSpanish ? 'A → Z' : 'A → Z'}</SelectItem>
                  <SelectItem value="alpha-desc">{isSpanish ? 'Z → A' : 'Z → A'}</SelectItem>
                  <SelectItem value="due-asc">{isSpanish ? 'Fecha (más pronto)' : 'Due date (soonest)'}</SelectItem>
                  <SelectItem value="due-desc">{isSpanish ? 'Fecha (más lejano)' : 'Due date (latest)'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Todo list */}
          <Card data-testid="todo-list-card">
            <CardHeader className="py-3 px-4">
              <CardTitle className="flex items-center justify-between text-base">
                <span>{isSpanish ? 'Tus Tareas' : 'Your Tasks'}</span>
                <span className="text-sm font-normal text-muted-foreground">
                  {filteredTodos.filter(t => !t.completed).length} {isSpanish ? 'pendientes' : 'pending'}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-3 pb-3">
              {filteredTodos.length === 0 ? (
                <div className="text-center py-10 text-muted-foreground">
                  <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">{isSpanish ? 'No hay tareas. Toca + para agregar.' : 'No tasks yet. Tap + to add one.'}</p>
                </div>
              ) : (
                <ul className="space-y-1.5">
                  {filteredTodos.map((todo) => {
                    const cat = getCategoryById(todo.category);
                    const isDragging = draggedId === todo.id;
                    const isDragOver = dragOverId === todo.id;

                    return (
                      <li
                        key={todo.id}
                        draggable={!editingId}
                        onDragStart={(e) => handleDragStart(e, todo.id)}
                        onDragOver={(e) => handleDragOver(e, todo.id)}
                        onDrop={(e) => handleDrop(e, todo.id)}
                        onDragEnd={handleDragEnd}
                        data-testid={`todo-item-${todo.id}`}
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all duration-200 ${
                          todo.completed
                            ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 opacity-60 scale-[0.98]'
                            : isDragOver
                              ? 'bg-[#00E5CC]/10 border-[#00E5CC]'
                              : isDragging
                                ? 'opacity-40 scale-95'
                                : 'bg-card hover:border-primary/30'
                        }`}
                      >
                        {/* Drag handle */}
                        <GripVertical className="h-4 w-4 text-muted-foreground/40 cursor-grab shrink-0" />

                        <Checkbox
                          checked={todo.completed}
                          onCheckedChange={() => toggleTodo(todo.id)}
                          className="data-[state=checked]:bg-[#00E5CC] data-[state=checked]:border-[#00E5CC] shrink-0"
                          data-testid={`todo-checkbox-${todo.id}`}
                        />

                        {/* Category dot */}
                        {cat && <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: cat.color }} title={cat.name} />}

                        {/* Text / Edit mode */}
                        {editingId === todo.id ? (
                          <div className="flex-1 flex flex-col gap-1">
                            <div className="flex gap-1">
                              <Input
                                ref={editInputRef}
                                value={editText}
                                onChange={(e) => setEditText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(todo.id); if (e.key === 'Escape') cancelEdit(); }}
                                className="h-7 text-sm flex-1"
                                data-testid={`todo-edit-input-${todo.id}`}
                              />
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => saveEdit(todo.id)} data-testid={`todo-save-${todo.id}`}>
                                <Check className="h-3 w-3 text-green-600" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={cancelEdit}>
                                <X className="h-3 w-3 text-red-500" />
                              </Button>
                            </div>
                            <Select value={editCategory || 'all'} onValueChange={(val) => setEditCategory(val)}>
                              <SelectTrigger className="h-7 text-xs w-36">
                                <SelectValue placeholder={isSpanish ? 'Categoría' : 'Category'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">{isSpanish ? 'Sin categoría' : 'No category'}</SelectItem>
                                {categories.map(cat => (
                                  <SelectItem key={cat.id} value={cat.id}>
                                    <span className="flex items-center gap-1">
                                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                      {cat.name}
                                    </span>
                                  </SelectItem>
                                ))}
                                {(() => {
                                  const defaults = isSpanish ? DEFAULT_CATEGORIES_ES : DEFAULT_CATEGORIES;
                                  const suggestions = defaults.filter(d => !categories.find(c => c.id === d.id));
                                  if (suggestions.length === 0) return null;
                                  return suggestions.map(cat => (
                                    <SelectItem key={cat.id} value={cat.id}>
                                      <span className="flex items-center gap-1 opacity-60">
                                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                        {cat.name}
                                      </span>
                                    </SelectItem>
                                  ));
                                })()}
                              </SelectContent>
                            </Select>
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => !todo.completed && startEdit(todo)} data-testid={`todo-text-${todo.id}`}>
                            <span className={`text-sm ${todo.completed ? 'line-through text-muted-foreground' : ''}`}>
                              {todo.text}
                            </span>
                            {todo.dueDate && (() => {
                              const label = getDueDateLabel(todo.dueDate);
                              return label ? (
                                <span className={`ml-2 text-[10px] ${label.color}`}>
                                  <CalendarDays className="h-2.5 w-2.5 inline mr-0.5" />
                                  {label.text}
                                </span>
                              ) : null;
                            })()}
                            {todo.recurrence && (
                              <span className="ml-2 text-[10px] text-[#7C3AED]">
                                <Repeat className="h-2.5 w-2.5 inline mr-0.5" />
                                {RECURRENCE_OPTIONS.find(o => o.value === todo.recurrence)?.[isSpanish ? 'labelEs' : 'label']}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Actions */}
                        {!todo.completed && editingId !== todo.id && (
                          <div className="flex items-center gap-0.5 shrink-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(todo)} data-testid={`todo-edit-${todo.id}`}>
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteTodo(todo.id)} data-testid={`todo-delete-${todo.id}`}>
                              <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          {todos.length > 0 && (
            <p className="text-center text-xs text-muted-foreground mt-3">
              {isSpanish
                ? 'Arrastra para reordenar. Toca el texto para editar. Las tareas recurrentes se regeneran al completar.'
                : 'Drag to reorder. Tap text to edit. Recurring tasks auto-regenerate.'}
            </p>
          )}
          </>)}
        </div>
      </section>

      <BottomNav />
    </div>
  );
};

export default TodoPage;
